import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    
    // Default to light theme
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Common theme classes for admin pages
export const adminTheme = {
  // Backgrounds
  pageBg: (isDark) => isDark ? 'bg-slate-950' : 'bg-slate-50',
  cardBg: (isDark) => isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200 shadow-sm',
  cardHover: (isDark) => isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
  inputBg: (isDark) => isDark ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400',
  tableBg: (isDark) => isDark ? 'bg-slate-900' : 'bg-white',
  tableRowHover: (isDark) => isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
  tableRowAlt: (isDark) => isDark ? 'bg-slate-800/50' : 'bg-slate-50',
  
  // Text colors
  textPrimary: (isDark) => isDark ? 'text-white' : 'text-slate-900',
  textSecondary: (isDark) => isDark ? 'text-slate-400' : 'text-slate-600',
  textMuted: (isDark) => isDark ? 'text-slate-500' : 'text-slate-400',
  
  // Borders
  border: (isDark) => isDark ? 'border-slate-800' : 'border-slate-200',
  borderLight: (isDark) => isDark ? 'border-slate-700' : 'border-slate-300',
  
  // Header/Navbar
  headerBg: (isDark) => isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
  
  // Accent backgrounds for stats
  accentBg: (isDark) => isDark ? 'bg-slate-800' : 'bg-slate-100',
  
  // Dropdown menus
  dropdownBg: (isDark) => isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
  dropdownItem: (isDark) => isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100',
};

